/* ==========================================================================
   Better Pet & Co · product page

   Vanilla, no dependencies, deferred. Everything in here is an enhancement:
   with the script blocked the page still reads, the variant <select> in the
   <noscript> block still submits, and the form still POSTs to /cart/add the
   ordinary way. Nothing below is load-bearing for buying something.

   Two rules shape the code:
   - No scroll listeners. Gallery position and the sticky bar both run on
     IntersectionObserver, so nothing of ours executes while a thumb is moving.
   - Errors are shown as Shopify wrote them. "You can only add 3 of this to
     your cart" tells a customer what to do next; "Oops, something went wrong"
     tells them to leave.
   ========================================================================== */

(function () {
  'use strict';

  /* Loaded by more than one section — the buy box brings it, and so does the
     questions section, which also runs on collection pages where there is no
     buy box. Two script tags for one file means every handler in it binds
     twice, and a toggle that fires twice per click lands back where it
     started. That is exactly how the "read more" buttons shipped dead.
     One run per page, whatever asks for it. */
  if (window.__bpProductInit) return;
  window.__bpProductInit = true;

  'use strict';

  var root = document.querySelector('[data-bp-product-root]');
  if (!root) return;

  var money = function (cents) {
    return 'CAD $' + (cents / 100).toFixed(2);
  };

  /* ---- Gallery ----------------------------------------------------------- */

  (function gallery() {
    var wrap = document.querySelector('[data-bp-gallery]');
    if (!wrap) return;

    var track = wrap.querySelector('[data-bp-gallery-track]');
    var slides = wrap.querySelectorAll('[data-bp-slide]');
    var dots = wrap.querySelectorAll('[data-bp-dot]');
    var thumbs = wrap.querySelectorAll('[data-bp-thumb]');
    if (!track || !slides.length) return;

    /* Gallery videos are rendered without `autoplay` — see the comment in
       bp-gallery.liquid. Autoplay overrides preload="none", so every video in
       the gallery downloaded in full on page load, off-screen frames included.
       Playback lives here instead: the frame on screen plays, the rest stay at
       their poster and cost nothing until someone swipes to them. */
    var videos = wrap.querySelectorAll('[data-bp-slide] video');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Set the first time the visitor actually moves the gallery. Until then
       nothing but the frame on screen is allowed to fetch anything.

       The neighbouring frame used to be warmed to `preload="metadata"` on
       load, on the theory that metadata is a few dozen kilobytes. WebKit does
       not honour that on these files: measured on an iPhone against the live
       page on 2026-09-05, the neighbour of the opening frame pulled its whole
       7.1 MB 1080p mp4 before anything else on the page had finished, which
       was three quarters of the weight of the entire page for a frame nobody
       had swiped to. Chromium fetches a few hundred kilobytes for the same
       markup, which is why it never showed up here.

       So the warm-up waits for a gesture. Somebody who swipes once has told
       us they are looking through the frames and the next one is worth having
       ready; somebody who lands from an ad and reads down the page never pays
       for it. */
    var browsed = false;

    var playActive = function (index) {
      Array.prototype.forEach.call(videos, function (video) {
        var slide = video.closest('[data-bp-slide]');
        if (!slide) return;

        var distance = Math.abs(Number(slide.dataset.bpSlide) - index);

        if (distance !== 0) {
          video.pause();
          // The frame next door gets its metadata so that a swipe starts
          // playing instead of sitting on a still — but only once the visitor
          // has moved the gallery at least once. See `browsed` above.
          if (browsed && distance === 1 && video.preload === 'none') {
            video.preload = 'metadata';
          }
          return;
        }
        if (reduced || !video.paused) return;
        if (video.preload !== 'auto') video.preload = 'auto';

        // Muted playback is allowed without a gesture, but a browser may still
        // refuse (a data saver, a background tab). The poster stays up in that
        // case, which is a fine place to end up, so the rejection is dropped.
        var started = video.play();
        if (started && started.catch) started.catch(function () {});
      });
    };

    var mark = function (index) {
      var apply = function (nodes, attr) {
        Array.prototype.forEach.call(nodes, function (node) {
          node.setAttribute('aria-current', String(Number(node.dataset[attr]) === index));
        });
      };
      apply(dots, 'bpDot');
      apply(thumbs, 'bpThumb');
      playActive(index);
    };

    // The track scrolls horizontally at every width, so this never moves the
    // page: clicking a thumbnail swaps the frame in place rather than jumping
    // the reader somewhere else on the page.
    var goTo = function (index) {
      var slide = slides[index];
      if (!slide) return;

      var left = slide.offsetLeft - track.offsetLeft;
      var start = track.scrollLeft;
      track.scrollTo({ left: left, behavior: 'smooth' });

      // Smooth scrolling is ignored outright by some browsers on a container
      // with scroll-snap, and the request is dropped without an error: the
      // gallery simply does not move. Nothing here should depend on the
      // animation, so if the track has not shifted at all a quarter of a
      // second later, jump. A hard cut is a worse gallery than a glide and a
      // far better one than a dead thumbnail.
      window.setTimeout(function () {
        if (track.scrollLeft === start && start !== left) track.scrollLeft = left;
      }, 250);

      browsed = true;
      mark(index);
    };

    Array.prototype.forEach.call(dots, function (dot) {
      dot.addEventListener('click', function () {
        goTo(Number(dot.dataset.bpDot));
      });
    });

    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(Number(thumb.dataset.bpThumb));
      });
    });

    // A swipe moves the track without going through goTo, so the gesture is
    // caught here as well. Passive: nothing is prevented, this only records
    // that the visitor has started looking.
    track.addEventListener('touchstart', function () { browsed = true; }, { passive: true });
    track.addEventListener('wheel', function () { browsed = true; }, { passive: true });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) mark(Number(entry.target.dataset.bpSlide));
          });
        },
        // Watched against the track, not the viewport: the frames move
        // sideways inside it, and against the viewport every frame would read
        // as visible at once.
        { root: track, threshold: 0.6 }
      );
      Array.prototype.forEach.call(slides, function (slide) {
        observer.observe(slide);
      });
    }

    /* The colour swatches move the gallery to the frame of the colour that was
       clicked. The picker cannot call goTo directly — it lives in another
       closure — so the gallery listens for an event instead of exporting a
       function. One direction of dependency, and the picker still works on a
       page that has no gallery at all.

       Addressed by media id rather than by index. An index would be right until
       somebody reorders the images in the admin, and then it would be silently
       wrong: still a number, still a valid frame, just not the one the customer
       asked for. */
    document.addEventListener('bp:show-media', function (event) {
      var wanted = String(event.detail && event.detail.mediaId);
      if (!wanted || wanted === 'null') return;

      for (var i = 0; i < slides.length; i += 1) {
        if (String(slides[i].dataset.bpMedia) === wanted) {
          goTo(i);
          return;
        }
      }
    });

    /* Lightbox. A <dialog>, so Escape and the focus trap come from the
       platform rather than from three hundred bytes of our own keyboard code. */
    var lightbox = document.querySelector('[data-bp-lightbox]');
    if (!lightbox || typeof lightbox.showModal !== 'function') return;

    var img = lightbox.querySelector('[data-bp-lightbox-img]');

    wrap.addEventListener('click', function (event) {
      var zoom = event.target.closest('[data-bp-zoom]');
      if (!zoom) return;
      img.src = zoom.dataset.bpZoom;
      img.alt = zoom.dataset.bpZoomAlt || '';
      lightbox.showModal();
    });

    lightbox.addEventListener('click', function (event) {
      if (event.target.closest('[data-bp-lightbox-close]') || event.target === lightbox) {
        lightbox.close();
      }
    });
  })();

  /* ---- Quantity ---------------------------------------------------------- */

  (function quantity() {
    var input = root.querySelector('[data-bp-qty-input]');
    if (!input) return;

    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-bp-qty]');
      if (!button) return;
      var next = Number(input.value) + Number(button.dataset.bpQty);
      input.value = String(Math.max(1, next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  })();

  /* ---- Variants ---------------------------------------------------------- */

  /* The price block, rebuilt on a variant change.

     It has to match what bp-price.liquid renders on the server, element for
     element, or the price changes shape the first time somebody picks a
     different size. The numbers themselves are not worked out here: the
     percentage and the was-price come from the variant JSON, which Liquid
     built with the same flooring rule. One place decides, two places print. */
  function priceMarkup(variant) {
    if (!variant.percentOff || !variant.compareAtText) {
      return '<p class="bp-price">' + variant.priceText + '</p>';
    }
    return (
      '<p class="bp-price bp-price--sale">' +
      '<span class="bp-price__now">' +
      '<span class="bp-visually-hidden">Sale price </span>' +
      variant.priceText +
      '</span>' +
      '<s class="bp-price__was">' +
      '<span class="bp-visually-hidden">Regular price </span>' +
      variant.compareAtText +
      '</s>' +
      '<span class="bp-price__off">' +
      variant.percentOff +
      '% off</span>' +
      '</p>'
    );
  }

  var form = root.querySelector('[data-bp-form]');
  var current = null;

  (function variants() {
    var source = root.querySelector('[data-bp-variants]');
    if (!source || !form) return;

    var data;
    try {
      data = JSON.parse(source.textContent);
    } catch (error) {
      return;
    }

    var idField = form.querySelector('[data-bp-variant-id]');
    var addButton = form.querySelector('[data-bp-add]');
    var label = form.querySelector('[data-bp-add-label]');
    var priceLive = root.querySelector('[data-bp-price-live]');
    var stickyPrice = document.querySelector('[data-bp-sticky-price]');

    var selected = function () {
      var chosen = [];
      root.querySelectorAll('[data-bp-option]').forEach(function (group) {
        var checked = group.querySelector('input[data-bp-option-input]:checked');
        var select = group.querySelector('select[data-bp-option-input]');
        chosen.push(checked ? checked.value : select ? select.value : null);
      });
      return chosen;
    };

    // Which frame the gallery is showing because of a variant choice. `apply`
    // also runs once on load, and that run only records the frame: the page
    // opens on whichever image the gallery starts with, and scrolling it
    // somewhere else before the reader has touched anything is a jump nobody
    // asked for.
    var shownMediaId = null;
    var firstRun = true;

    var apply = function () {
      // Pack tiers name the variant outright, so there is nothing to match on.
      // They are checked first because when they exist they are the only
      // control — the option pills are not rendered at all.
      var pack = root.querySelector('[data-bp-bundle-input]:checked');

      if (pack) {
        current =
          data.find(function (variant) {
            return String(variant.id) === pack.value;
          }) || null;
      } else {
        var chosen = selected();
        if (!chosen.length) {
          current = data[0];
        } else {
          current =
            data.find(function (variant) {
              return variant.options.every(function (option, index) {
                return option === chosen[index];
              });
            }) || null;
        }
      }

      if (!current) {
        // A combination that does not exist. Say so rather than leaving a
        // button that fails on click.
        if (priceLive) priceLive.innerHTML = '<p class="bp-price bp-price__unavailable">This combination isn\'t available</p>';
        if (addButton) {
          addButton.disabled = true;
          if (label) label.textContent = 'Unavailable';
        }
        return;
      }

      if (idField) idField.value = String(current.id);
      if (priceLive) priceLive.innerHTML = priceMarkup(current);
      if (stickyPrice) stickyPrice.textContent = current.priceText;

      if (addButton) {
        addButton.disabled = !current.available;
        // textContent, not innerHTML: the label is two fixed strings now that
        // the price has come out of it, and there is no reason for a variant
        // change to be able to write markup into the button.
        if (label) {
          label.textContent = current.available ? 'Add to cart' : 'Back in stock soon';
        }
      }

      // Move the gallery to the frame this variant belongs to. Announced
      // rather than called: the gallery owns its own scrolling, and this file
      // has no business reaching into that closure.
      //
      // Only fired when the choice actually changed the frame. Without that
      // guard, picking a size on a product whose sizes share one photograph
      // would scroll the gallery back to the top image every time — technically
      // correct and infuriating, because the reader was looking at the detail
      // shot on purpose.
      if (current.mediaId && current.mediaId !== shownMediaId) {
        var wasFirst = firstRun;
        shownMediaId = current.mediaId;
        if (!wasFirst) {
          document.dispatchEvent(
            new CustomEvent('bp:show-media', { detail: { mediaId: current.mediaId } })
          );
        }
      }
      firstRun = false;

      // The URL follows the choice so a shared link opens on the same variant.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', current.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    root.addEventListener('change', function (event) {
      if (event.target.matches('[data-bp-option-input], [data-bp-bundle-input]')) apply();
    });

    apply();
  })();

  /* ---- Add to cart ------------------------------------------------------- */

  // Drawer refs and the line renderer sit out here because two things now open
  // this drawer: the buy box, and the set section further down the page. They
  // were local to `cart` while it was the only caller.
  var drawer = document.querySelector('[data-bp-drawer]');
  var drawerBody = drawer && drawer.querySelector('[data-bp-drawer-body]');
  var drawerTotal = drawer && drawer.querySelector('[data-bp-drawer-total]');
  var bubble = document.querySelector('.cart-count-bubble');

  var renderCart = function (cart) {
    if (!drawerBody) return;

    drawerBody.innerHTML = cart.items
      .map(function (item) {
        return (
          '<div class="bp-drawer__line">' +
          (item.image
            ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') + '" alt="" width="60" height="60" loading="lazy">'
            : '') +
          '<div><p>' +
          item.product_title +
          '</p><p class="bp-drawer__line-qty">Qty ' +
          item.quantity +
          '</p></div>' +
          '<span class="bp-drawer__line-price">' +
          money(item.final_line_price) +
          '</span></div>'
        );
      })
      .join('');

    if (drawerTotal) drawerTotal.textContent = money(cart.total_price);
    if (bubble) bubble.textContent = String(cart.item_count);
  };


  /* ---- The bundle app's block -------------------------------------------

     Kaching Bundles injects <kaching-bundle> as a direct child of .bp-buybox,
     high up, and offers no setting for where. The owner wants it directly above
     Add to cart, under the size pickers, which is where the decision it asks
     about actually arrives.

     So the theme renders an empty slot in that position and moves the element
     into it. Moving rather than copying: it is their custom element with their
     listeners on it, and a clone would be a dead picture of a working control.

     Watched rather than moved once, because the app renders after us and can
     re-render on a variant change. The observer disconnects as soon as the
     element is where it belongs, and gives up after ten seconds so a page
     without the app is not left watching for something that is never coming. */
  (function appBundle() {
    var slot = document.querySelector('[data-bp-app-bundle-slot]');
    if (!slot) return;

    var move = function () {
      var block = document.querySelector('kaching-bundle, .kaching-bundles');
      if (!block || slot.contains(block)) return false;
      slot.appendChild(block);
      return true;
    };

    if (move()) return;

    var observer = new MutationObserver(function () {
      if (move()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(function () {
      observer.disconnect();
    }, 10000);
  })();

  (function cart() {
    if (!form) return;

    var addButton = form.querySelector('[data-bp-add]');
    var errorNode = form.querySelector('[data-bp-error]');

    form.addEventListener('submit', function (event) {
      if (!window.fetch || !drawer || typeof drawer.showModal !== 'function') return;
      event.preventDefault();

      if (errorNode) errorNode.textContent = '';
      addButton.classList.add('is-loading');
      addButton.setAttribute('aria-disabled', 'true');

      // With a bundle row selected the button is buying more than one thing,
      // which a form POST cannot express. Same endpoint, JSON body.
      var bundle = setRows && setRows.items();

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: bundle
          ? { 'Content-Type': 'application/json', Accept: 'application/json' }
          : { Accept: 'application/json' },
        body: bundle ? JSON.stringify({ items: bundle }) : new FormData(form)
      })
        .then(function (response) {
          return response.json().then(function (json) {
            if (!response.ok) throw json;
            return json;
          });
        })
        .then(function () {
          return fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } }).then(
            function (response) {
              return response.json();
            }
          );
        })
        .then(function (cart) {
          renderCart(cart);

          // The promise, checked against Shopify's own arithmetic. If the buy
          // box advertised a bundle price and the cart came back without a
          // discount on it, the automatic discount is missing or expired and
          // the customer is about to pay more than the page said.
          if (setRows && setRows.expected() > 0 && !Number(cart.total_discount) && setRows.flag && drawerBody) {
            drawerBody.insertAdjacentHTML(
              'afterbegin',
              '<p class="bp-set__flag">' + setRows.flag.textContent.trim() + '</p>'
            );
          }

          drawer.showModal();
        })
        .catch(function (json) {
          // Shopify's own wording. `description` is the readable one;
          // `message` is the terse fallback.
          if (errorNode) {
            errorNode.textContent =
              (json && (json.description || json.message)) || 'That did not go through. Please try again.';
          }
        })
        .finally(function () {
          addButton.classList.remove('is-loading');
          addButton.removeAttribute('aria-disabled');
        });
    });

    if (drawer) {
      drawer.addEventListener('click', function (event) {
        if (event.target.closest('[data-bp-drawer-close]')) drawer.close();
      });
    }

    // The closing CTA in section 9 adds straight from where the visitor is
    // standing. Its href points at the buy section, so with JS blocked it
    // still does something sensible instead of nothing.
    var closeCta = document.querySelector('[data-bp-close-add]');
    if (closeCta) {
      closeCta.addEventListener('click', function (event) {
        event.preventDefault();
        form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }
  })();

  /* ---- Sticky bar -------------------------------------------------------- */

  (function sticky() {
    var bar = document.querySelector('[data-bp-sticky]');
    var anchor = form && form.querySelector('[data-bp-add]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var buttonOut = false;
    var closeIn = false;

    var update = function () {
      var show = buttonOut && !closeIn;
      bar.hidden = !show;
      // hidden first, class second, or the transition has nothing to animate
      // from on the very first reveal.
      window.requestAnimationFrame(function () {
        bar.classList.toggle('is-visible', show);
      });
    };

    new IntersectionObserver(
      function (entries) {
        buttonOut = !entries[0].isIntersecting;
        update();
      },
      { threshold: 0 }
    ).observe(anchor);

    // Section 9 closes the page. Two add-to-cart buttons on screen at once
    // reads as nagging, so the bar stands down when the real one reappears.
    var closing = document.querySelector('[data-bp-close-section]');
    if (closing) {
      new IntersectionObserver(
        function (entries) {
          closeIn = entries[0].isIntersecting;
          update();
        },
        { threshold: 0 }
      ).observe(closing);
    }

    bar.querySelector('[data-bp-sticky-add]').addEventListener('click', function () {
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
    });
  })();

  /* ---- Demo video -------------------------------------------------------- */

  (function demo() {
    var wrap = document.querySelector('[data-bp-demo]');
    if (!wrap) return;

    var video = wrap.querySelector('video');
    var play = wrap.querySelector('[data-bp-demo-play]');
    if (!video) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      // Poster and a real play button. The visitor asked for less movement;
      // an autoplaying loop is exactly the movement they asked less of.
      video.removeAttribute('autoplay');
      video.removeAttribute('loop');
      video.pause();
      if (play) play.classList.add('is-shown');
    }

    if (play) {
      play.addEventListener('click', function () {
        video.play();
        play.classList.remove('is-shown');
      });
    }

    // preload="none" means nothing is fetched until we say so. Loading starts
    // when the section is close to the viewport, not on page load — the demo
    // is two screens below the LCP element and must not compete with it.
    if (!reduced && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) return;
          video.preload = 'auto';
          video.load();
          var attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(function () {});
          observer.disconnect();
        },
        { rootMargin: '200px' }
      );
      observer.observe(wrap);
    }
  })();

  /* ---- Videos that start when they come into view ------------------------ */

  /* The general case of the rule above, for sections that hold more than one
     clip. A video marked `data-bp-play-visible` ships without `autoplay`, so
     nothing of it is fetched on page load; this starts it when its own figure
     comes near, and pauses it again once it has gone. Any section can opt in by
     putting the attribute on the wrapper. */
  (function playWhenVisible() {
    var wraps = document.querySelectorAll('[data-bp-play-visible]');
    if (!wraps.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target.querySelector('video');
          if (!video) return;

          if (!entry.isIntersecting) {
            video.pause();
            return;
          }
          if (video.preload !== 'auto') video.preload = 'auto';
          if (!video.paused) return;

          var started = video.play();
          if (started && started.catch) started.catch(function () {});
        });
      },
      { rootMargin: '200px', threshold: 0.01 }
    );

    Array.prototype.forEach.call(wraps, function (wrap) {
      observer.observe(wrap);
    });
  })();

  /* ---- FAQ --------------------------------------------------------------- */

  (function faq() {
    var list = document.querySelector('[data-bp-faq]');
    if (!list) return;

    // One open at a time. <details> gives us the keyboard behaviour for free;
    // this only enforces the exclusivity.
    list.addEventListener('toggle', function (event) {
      var item = event.target;
      if (!item.open) return;
      list.querySelectorAll('details[open]').forEach(function (other) {
        if (other !== item) other.open = false;
      });
    }, true);
  })();

  /* ---- Bundle rows in the buy box ---------------------------------------- */

  // Three radios: just this, this plus one, this plus both. Everything they
  // show is arithmetic on live variant prices, and the row cannot be bought
  // until every companion in it has a variant chosen — a total on screen that
  // the cart would disagree with is the one outcome this whole control exists
  // to prevent.

  var setRows = (function () {
    var box = document.querySelector('[data-bp-setrows]');
    if (!box) return null;

    var radios = Array.prototype.slice.call(box.querySelectorAll('[data-bp-setrow]'));
    if (!radios.length) return null;

    var picks = {};
    Array.prototype.forEach.call(box.querySelectorAll('[data-bp-setpick]'), function (node) {
      picks[node.dataset.bpSetpick] = node;
    });

    var anchorPrice = Number(box.dataset.bpSetAnchor) || 0;
    var pct = { a: Number(box.dataset.bpSetPct2) || 0, ab: Number(box.dataset.bpSetPct3) || 0 };

    var addButton = form && form.querySelector('[data-bp-add]');
    var addLabel = form && form.querySelector('[data-bp-add-label]');
    var priceLive = root.querySelector('[data-bp-price-live]');
    var stickyPrice = document.querySelector('[data-bp-sticky-price]');

    var chosenRow = function () {
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) return radios[i];
      }
      return radios[0];
    };

    var keysFor = function (radio) {
      var raw = (radio.dataset.bpSetWith || '').trim();
      return raw ? raw.split(',') : [];
    };

    // One companion's id and price, whichever kind it is. A single-variant
    // product carries both on the element; one with variants carries them on
    // the option the visitor chose, and carries nothing until they do.
    var readPick = function (key) {
      var node = picks[key];
      if (!node) return { id: '', price: 0, needs: '' };

      if (node.dataset.bpSetId) {
        return { id: node.dataset.bpSetId, price: Number(node.dataset.bpSetPrice) || 0, needs: '' };
      }

      var select = node.querySelector('[data-bp-setselect]');
      if (!select) return { id: '', price: 0, needs: '' };

      var option = select.options[select.selectedIndex];
      if (!select.value) {
        var label = node.querySelector('.bp-setrows__pick-label');
        return { id: '', price: 0, needs: label ? label.textContent.trim() : 'an option' };
      }
      return { id: select.value, price: Number(option.dataset.price) || 0, needs: '' };
    };

    var anchorId = function () {
      var field = form && form.querySelector('[data-bp-variant-id]');
      if (field && field.value) return field.value;
      var checked = form && form.querySelector('input[name="id"]:checked');
      return checked ? checked.value : box.dataset.bpSetAnchorId;
    };

    var state = { items: null, expected: 0 };

    var draw = function () {
      var radio = chosenRow();
      var keys = keysFor(radio);
      var percent = keys.length === 2 ? pct.ab : keys.length === 1 ? pct.a : 0;

      // Reveal only the pickers this row actually needs.
      Object.keys(picks).forEach(function (key) {
        picks[key].hidden = keys.indexOf(key) === -1 || !picks[key].querySelector('[data-bp-setselect]');
      });

      var full = anchorPrice;
      var ids = [];
      var missing = '';

      keys.forEach(function (key) {
        var pick = readPick(key);
        if (!pick.id) {
          if (!missing) missing = pick.needs || 'an option';
          return;
        }
        full += pick.price;
        ids.push(pick.id);
      });

      var complete = ids.length === keys.length;
      var saving = complete && percent > 0 ? Math.round((full * percent) / 100) : 0;
      var net = full - saving;

      // Every row keeps its own total up to date, not just the selected one:
      // the figures are what the visitor is comparing, so they have to be right
      // before the click that selects the row, not after it.
      radios.forEach(function (other) {
        var otherKeys = keysFor(other);
        var otherPct = otherKeys.length === 2 ? pct.ab : otherKeys.length === 1 ? pct.a : 0;
        var otherFull = anchorPrice;
        var known = true;

        otherKeys.forEach(function (key) {
          var pick = readPick(key);
          if (!pick.id) {
            known = false;
            var node = picks[key];
            var select = node && node.querySelector('[data-bp-setselect]');
            // Nothing chosen yet, so the cheapest variant is the only figure
            // that is true — and it is labelled "From" so it cannot be read as
            // the price.
            if (select) {
              var lowest = 0;
              Array.prototype.forEach.call(select.options, function (option) {
                var value = Number(option.dataset.price) || 0;
                if (value && (!lowest || value < lowest)) lowest = value;
              });
              otherFull += lowest;
            }
            return;
          }
          otherFull += pick.price;
        });

        var otherSave = otherPct > 0 ? Math.round((otherFull * otherPct) / 100) : 0;
        var totalNode = other.parentNode.querySelector('[data-bp-set-total]');
        var wasNode = other.parentNode.querySelector('[data-bp-set-was]');

        if (totalNode) {
          totalNode.textContent = (known ? '' : 'From ') + money(otherFull - otherSave);
        }
        if (wasNode) {
          var strike = wasNode.querySelector('s');
          var save = wasNode.querySelector('.bp-bundle__save');
          if (strike) strike.textContent = money(otherFull);
          if (save) save.textContent = 'Save ' + money(otherSave);
          wasNode.hidden = otherSave === 0;
        }
      });

      if (priceLive) priceLive.innerHTML = '<p class="bp-price">' + money(net) + '</p>';
      if (stickyPrice) stickyPrice.textContent = money(net);

      if (addButton) {
        addButton.disabled = !complete;
        if (addLabel) {
          addLabel.textContent = complete ? 'Add to cart' : 'Choose ' + missing.toLowerCase() + ' first';
        }
      }

      state.expected = saving;
      state.items = keys.length === 0 ? null : [{ id: anchorId(), quantity: 1 }].concat(
        ids.map(function (id) {
          return { id: id, quantity: 1 };
        })
      );
    };

    box.addEventListener('change', draw);
    // An option pill on the anchor rewrites the price and the button label, so
    // the rows have to reassert themselves after it rather than before.
    if (form) form.addEventListener('change', function () { window.setTimeout(draw, 0); });

    box.hidden = false;
    draw();

    return {
      items: function () {
        return state.items;
      },
      expected: function () {
        return state.expected;
      },
      flag: box.querySelector('[data-bp-set-flag]')
    };
  })();

  /* ------------------------------------------------------------------ *
   * The description's lid.
   *
   * Measured rather than assumed: a description under the clamp gets no
   * button at all, because a "read more" that reveals two lines is worse
   * than no button. Everything starts expanded in the markup, so without
   * this script the full text is on the page.
   * ------------------------------------------------------------------ */
  (function desc() {
    var wrap = document.querySelector('[data-bp-desc]');
    if (!wrap) return;

    var body = wrap.querySelector('[data-bp-desc-body]');
    var button = wrap.querySelector('[data-bp-desc-more]');
    if (!body || !button) return;

    var LIMIT = 460;
    var narrow = window.matchMedia('(max-width: 1023px)');

    /* Two faces, one piece of text. Below 1024px this is the third row of the
       accordion list under the button, shut, with the label as its header.
       Above it, it is the description in the column the gallery leaves empty,
       opened far enough to be worth reading. Duplicating the markup would
       have been simpler and would have put the whole description on the page
       twice, which is a thing Google counts. */
    var label = function () {
      if (narrow.matches) return 'Full description';
      return wrap.getAttribute('data-collapsed') === 'false'
        ? 'Show less'
        : 'Read the full description';
    };

    var measure = function () {
      if (narrow.matches) {
        /* Always shut on a phone, however short the text: it is an accordion
           there, and one that is already open is a wall. */
        if (!wrap.hasAttribute('data-collapsed')) {
          wrap.setAttribute('data-collapsed', 'true');
        }
        button.hidden = false;
      } else if (wrap.getAttribute('data-collapsed') !== 'false') {
        if (body.scrollHeight <= LIMIT) {
          wrap.removeAttribute('data-collapsed');
          button.hidden = true;
        } else {
          wrap.setAttribute('data-collapsed', 'true');
          button.hidden = false;
        }
      }
      button.querySelector('span').textContent = label();
    };

    button.addEventListener('click', function () {
      var collapsed = wrap.getAttribute('data-collapsed') !== 'false';
      wrap.setAttribute('data-collapsed', collapsed ? 'false' : 'true');
      button.querySelector('span').textContent = label();
      if (!collapsed && !narrow.matches) wrap.scrollIntoView({ block: 'start' });
    });

    window.addEventListener('resize', measure);

    measure();
    // Images arrive late and change the height they were measured at.
    window.addEventListener('load', measure);
  })();

  /* ------------------------------------------------------------------ *
   * Story rows: one paragraph, then a button.
   *
   * Measured, not assumed. A row whose text already fits gets no button,
   * because a "read more" that reveals two lines is worse than none.
   * ------------------------------------------------------------------ */
  (function stories() {
    var wraps = document.querySelectorAll('[data-bp-more]');
    if (!wraps.length) return;

    Array.prototype.forEach.call(wraps, function (wrap) {
      var body = wrap.querySelector('[data-bp-more-body]');
      var button = wrap.querySelector('[data-bp-more-btn]');
      if (!body || !button) return;

      var LIMIT = 150; /* about the height of one paragraph */
      var phone = window.matchMedia('(max-width: 749px)');

      /* Only on a phone. On a desktop the row is two columns and the text sits
         beside its picture, where four paragraphs are a column of text rather
         than four screens of scrolling — and a button that hides two thirds of
         the argument there costs more than it saves. */
      var measure = function () {
        if (!phone.matches) {
          wrap.removeAttribute('data-collapsed');
          button.hidden = true;
          return;
        }
        if (wrap.getAttribute('data-collapsed') === 'false') return;
        if (body.scrollHeight <= LIMIT) {
          wrap.removeAttribute('data-collapsed');
          button.hidden = true;
          return;
        }
        wrap.setAttribute('data-collapsed', 'true');
        button.hidden = false;
      };

      button.addEventListener('click', function () {
        var collapsed = wrap.getAttribute('data-collapsed') !== 'false';
        wrap.setAttribute('data-collapsed', collapsed ? 'false' : 'true');
        button.querySelector('span').textContent = collapsed ? 'Show less' : 'Read more';
      });

      measure();
      window.addEventListener('resize', measure);
    });
  })();

})();
